import Button from '@/common/components/base/button/Button';
import DateInput from '@/common/components/base/input/dateInput/DateInput';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import FixToBottom from '@/common/components/layouts/FixToBottom';
import { insuranceOption } from '@/common/queries/selections/insurance/insuranceOption';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import convertTimestampToShamsi from '@/common/utils/convertTimestampToShamsi';
import { INSURANCE_NUMBER_RULE } from '@/common/utils/rules/insurance.rules';
import {
  validateNotFutureDate,
  validateNotPastDate,
} from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import { getPropertyInsuranceOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getPropertyInsurance/getPropertyInsuranceOptions';
import { patchPropertyInsuranceOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchPropertyInsurance/patchPropertyInsuranceOptions';
import { PropertyInsuranceFormType } from '@/modules/adjusterReportModules/IncidentParticipants/types/PropertyInsurance.type';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Flex } from 'styled-system/jsx';

const EditDamagedPropertyInsurance = () => {
  const methods = useForm<
    Pick<
      PropertyInsuranceFormType,
      | 'insurance_expiry_date'
      | 'insurance_expiry_date_last_year'
      | 'insurance_start_date'
      | 'insurance_number'
      | 'insurance'
    >
  >({
    mode: 'onChange',
  });
  const { id, damagedId } = useParams();
  const [name, setName] = useState<string | null>(null);
  const navigate = useNavigate();
  const { data: insurances, isPending } = useQuery(
    insuranceOption({ name: name }),
  );
  const clientQuery = useQueryClient();

  const { data: damagedInsuranceData, isPending: damagedPending } = useQuery(
    getPropertyInsuranceOptions({ id: +id!, damaged_id: +damagedId! }),
  );

  const { mutate } = useMutation(patchPropertyInsuranceOptions());
  const submitHandler = (data: PropertyInsuranceFormType) => {
    mutate(
      {
        id: +id!,
        damaged_id: +damagedId!,
        formData: {
          insurance_expiry_date: convertPersianToGregorian(
            data?.insurance_expiry_date!,
          ),
          insurance_expiry_date_last_year: convertPersianToGregorian(
            data?.insurance_expiry_date_last_year!,
          ),
          insurance: (data?.insurance?.value as number) ?? null,
          insurance_number: data?.insurance_number,

          insurance_start_date: convertPersianToGregorian(
            data?.insurance_start_date!,
          ),
        },
      },
      {
        onSuccess: () => {
          navigate(-1);
          clientQuery.invalidateQueries({
            queryKey: getPropertyInsuranceOptions({
              id: +id!,
              damaged_id: +damagedId!,
            }).queryKey,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (damagedInsuranceData) {
      methods.reset({
        insurance: {
          label: damagedInsuranceData?.insurance?.name as string,
          value: damagedInsuranceData?.insurance?.id as number,
        },
        insurance_number: damagedInsuranceData?.insurance_number,

        insurance_expiry_date: damagedInsuranceData?.insurance_expiry_date
          ? convertTimestampToShamsi(
              damagedInsuranceData?.insurance_expiry_date,
            )
          : null,
        insurance_expiry_date_last_year:
          damagedInsuranceData?.insurance_expiry_date_last_year
            ? convertTimestampToShamsi(
                damagedInsuranceData?.insurance_expiry_date_last_year,
              )
            : null,
        insurance_start_date: damagedInsuranceData?.insurance_start_date
          ? convertTimestampToShamsi(damagedInsuranceData?.insurance_start_date)
          : null,
      });
    }
  }, [damagedInsuranceData]);

  const startDateWatcher = useWatch({
    control: methods?.control,
    name: 'insurance_start_date',
  });
  const expiryDateWatcher = useWatch({
    control: methods?.control,
    name: 'insurance_expiry_date',
  });

  useEffect(() => {
    if (expiryDateWatcher) {
      methods.trigger('insurance_start_date');
    }
  }, [expiryDateWatcher]);

  return (
    <FormProvider {...methods}>
      <Flex direction="column" gap="1" pt={24} mb={24}>
        <ModalInput
          name="insurance"
          placeholder="انتخاب کنید"
          optionalLabel
          label="بیمه‌گر"
          title="بیمه‌گر"
          loading={isPending}
          searchOptions={{
            onChange: (e) => {
              setName(e);
            },
          }}
          options={insurances?.map((insurance) => ({
            label: insurance?.name as string,
            value: insurance?.id as number,
          }))}
        />
        <TextInput
          placeholder="شماره بیمه نامه را وارد کنید"
          name="insurance_number"
          label="شماره بیمه نامه"
          optionalLabel
          max={25}
          rules={{ ...INSURANCE_NUMBER_RULE }}
        />
        <Flex direction="row" marginBottom={'1rem'}>
          <DateInput
            label="تاریخ شروع"
            buttonText="ثبت"
            modalLabel="تاریخ شروع"
            name="insurance_start_date"
            isDefaultValueEmpty={true}
            rules={{
              validate: {
                notFuture: validateNotFutureDate,
                notExpiryDate: () => {
                  if (!expiryDateWatcher) return true;
                  if (!startDateWatcher) return true;

                  try {
                    const expiry = convertPersianToGregorian(expiryDateWatcher);
                    const start = convertPersianToGregorian(startDateWatcher);
                    if (expiry < start) {
                      return ' تاریخ شروع نمی‌تواند بعد از تاریخ انقضا باشد';
                    }
                    return true;
                  } catch (err) {
                    return 'خطا در تبدیل تاریخ';
                  }
                },
              },
            }}
            optionalLabel
            className={css({
              borderLeftRadius: '0px',
            })}
          />
          <DateInput
            label="تاریخ انقضا"
            buttonText="ثبت"
            modalLabel="تاریخ انقضا"
            optionalLabel
            isDefaultValueEmpty={true}
            rules={{
              validate: {
                notPast: validateNotPastDate,
                notExpiryDate: () => {
                  if (!expiryDateWatcher) return true;
                  if (!startDateWatcher) return true;

                  try {
                    const expiry = convertPersianToGregorian(expiryDateWatcher);
                    const start = convertPersianToGregorian(startDateWatcher);
                    if (expiry < start) {
                      return 'تاریخ انقضا نمی‌تواند قبل از تاریخ شروع باشد';
                    }
                    return true;
                  } catch (err) {
                    return 'خطا در تبدیل تاریخ';
                  }
                },
              },
            }}
            name="insurance_expiry_date"
            className={css({
              borderRightRadius: '0px',
            })}
          />
        </Flex>

        <DateInput
          label="تاریخ انقضا بیمه نامه سال قبل"
          buttonText="ثبت"
          optionalLabel
          modalLabel="تاریخ انقضا بیمه نامه سال قبل"
          isDefaultValueEmpty={true}
          name="insurance_expiry_date_last_year"
          rules={{ validate: validateNotFutureDate }}
        />
        <FixToBottom className={css({ bottom: 70 })}>
          <Button
            onClick={methods.handleSubmit(submitHandler)}
            type="submit"
            width="100%"
            marginTop="6px"
            disabled={
              damagedPending ||
              !methods?.formState?.isValid ||
              methods.formState.isSubmitting
            }
          >
            ثبت
          </Button>
        </FixToBottom>
      </Flex>
    </FormProvider>
  );
};
export default EditDamagedPropertyInsurance;
